import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { AccommodationDateTimeFields } from './Fields/AccommodationDateTimeFields'
import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateAccommodationItemViewModel } from '../Create/models'
import type { PatchAccommodationItemViewModel } from '../Edit/models'

import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import { useComponentRestrictions } from 'tripPlanner/hooks/Google/useComponentRestrictions'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ESTABLISHMENT_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

const GEO_DEP_KEYS: Array<string> = ['place.name']

interface Props {
  tripId: string
}

function AccommodationFields({ tripId }: Props) {
  const { required, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateAccommodationItemViewModel | PatchAccommodationItemViewModel
  >()
  const { control, setValue, watch, register } = methods

  useEnsureGeoPlace(methods, 'place', GEO_DEP_KEYS)
  const componentRestrictions = useComponentRestrictions(tripId)

  const place = watch('place')

  const handleAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      setValue(
        'place.name',
        selectedOption.details?.name || selectedOption.label,
      )

      setValue('place.type', selectedOption.prediction ? 'GEO' : 'CUSTOM')

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction
        setValue('place.id', place_id)
      }

      if (selectedOption.details) {
        const {
          geometry,
          formatted_address,
          international_phone_number,
          website,
          address_components,
        } = selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue('place.region', region)
          }
        }

        if (website) {
          setValue('url', website)
        }
        setValue('phone', international_phone_number)
        formatted_address && setValue('place.address', formatted_address)

        if (geometry?.location) {
          setValue('place.point.lat', geometry.location.lat)
          setValue('place.point.lng', geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handlePlaceNameChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      setValue(
        'place.name',
        selectedOption.details?.name || selectedOption.label,
      )

      handleAddressChange(selectedOption)
    },
    [handleAddressChange, setValue],
  )

  const accommodationLabel = intl.formatMessage({
    id: 'W23H3bkpAgz6wgtcU1em',
    defaultMessage: 'Accommodation',
    description: 'Form label for accommodation name field in trip item editor',
  })

  const accommodationPlaceholder = intl.formatMessage({
    id: 'q1t_5z2nw4mcrGIHryF0',
    defaultMessage: 'Search by hotel name or location',
    description: 'Placeholder text for accommodation search input in trip item editor',
  })

  const addressLabel = intl.formatMessage({
    id: 't6zuMleceZ_YAGBle3yb',
    defaultMessage: 'Address',
    description: 'Label for address display in accommodation trip item',
  })

  const priceLabel = intl.formatMessage({
    id: '6buj5aAOeeqnWrQBq-eB',
    defaultMessage: 'Price',
    description: 'Form label for price field in accommodation trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: '6gqhoJ11ii69lUKghjX8',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in accommodation trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: '-SE9GDbLBcG1lxBV0Jmy',
    defaultMessage: 'Note',
    description: 'Form label for notes field in accommodation trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: '11royVeeZsJKv8-4LbuQ',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field in the pricing or cost breakdown section',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQIYT78QXghI',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const notePlaceholder = intl.formatMessage({
    id: '8lL-HnK60ahgBfwDB2Vn',
    defaultMessage: 'Add note here',
    description: 'Input placeholder text for notes textarea field in the trip planner item editor modal',
  })

  return (
    <Group direction="vertical" gap={16}>
      <TripAddressAutocomplete
        {...register('place.name', { required })}
        required
        label={accommodationLabel}
        placeholder={accommodationPlaceholder}
        initPlaceId={place?.type === 'GEO' ? place.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ESTABLISHMENT_RESPONSE_DETAILS}
        onPlaceSelect={handlePlaceNameChange}
        startIcon={<LineSearchIcon />}
        autocompletionReqParam={{
          componentRestrictions,
        }}
      />
      {place?.address && (
        <AddressDisplay title={addressLabel} value={place.address} />
      )}
      <AccommodationDateTimeFields />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          size="tall"
          placeholder={notePlaceholder}
          label={noteLabel}
          resize="vertical"
        />
      </ShowMore>
    </Group>
  )
}

export default AccommodationFields
